import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { CriterionKey } from "../../lib/duo-rubric";
import { CRITERIA } from "../../lib/duo-rubric";
import { authedFetch } from "../../lib/api-client";
import type { ApiEnvelope } from "@a2/core/schemas";
import { useT } from "../../lib/use-t";

type FocusTip = {
  id: string;
  message: string;
  criterion?: CriterionKey;
  deel?: number;
  topicSlug?: string;
  topicTitle?: string;
};

type FocusResponse = { tips: FocusTip[] };

function badgeFor(tip: FocusTip): string | null {
  if (tip.criterion) {
    return CRITERIA.find((c) => c.key === tip.criterion)?.label ?? null;
  }
  if (tip.deel) return `Deel ${tip.deel}`;
  return null;
}

/**
 * "Waar te focussen" panel — renders the real /study/focus tips (NOT the
 * mockup's invented per-criterion average bars, which have no backend).
 * Same fetch shape as StudyTips; framed as a dashboard panel here.
 */
export function FocusCard() {
  const t = useT();
  const [tips, setTips] = useState<FocusTip[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch("/study/focus");
        if (!res.ok) {
          console.error(`[focus-card] HTTP ${res.status}`);
          if (!cancelled) setTips([]);
          return;
        }
        const json = (await res.json()) as ApiEnvelope<FocusResponse>;
        if (cancelled) return;
        setTips(json.ok ? json.data.tips : []);
      } catch (err) {
        console.error("[focus-card] fetch failed", err);
        if (!cancelled) setTips([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const frame = (children: React.ReactNode) => (
    <section
      aria-labelledby="focus-heading"
      className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-5 sm:p-6 shadow-[var(--shadow)] space-y-4"
    >
      <div>
        <h3 id="focus-heading" className="text-lg font-semibold tracking-tight">
          {t("history.voortgang.focusTitle")}
        </h3>
        <p className="text-sm text-[var(--ink-muted)] mt-1">
          {t("history.voortgang.focusSubtitle")}
        </p>
      </div>
      {children}
    </section>
  );

  if (tips === null) {
    return frame(
      <div className="h-20 animate-pulse rounded-lg bg-[var(--panel-soft)]/60" aria-hidden />,
    );
  }

  if (tips.length === 0) {
    return frame(
      <p className="font-serif-italic text-base text-[var(--ink-muted)]">
        {t("history.voortgang.focusEmpty")}
      </p>,
    );
  }

  // Top two tips — the weakest areas surface first from the API ordering.
  const top = tips.slice(0, 2);

  return frame(
    <div className="space-y-4">
      {top.map((tip) => {
        const badge = badgeFor(tip);
        return (
          <div key={tip.id} className="space-y-2">
            {badge ? (
              <span className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--accent)]">
                {badge}
              </span>
            ) : null}
            <p className="font-serif-italic text-base leading-relaxed text-[var(--ink)]">
              {tip.message}
            </p>
            {tip.topicSlug && tip.topicTitle ? (
              <Link
                to={`/spreken/leren/${tip.topicSlug}`}
                className="inline-flex items-center gap-1.5 text-sm text-[var(--accent)] hover:text-[var(--accent-hover)] transition-colors focus-visible:outline-none focus-visible:underline"
              >
                <span>{t("history.voortgang.focusToLesson")}</span>
                <span aria-hidden>→</span>
              </Link>
            ) : null}
          </div>
        );
      })}
    </div>,
  );
}
